import { InLineLoader } from "components/InlineLoader/InlineLoader";
import Dashboard from "containers/Dashboard/Dashboard";
import Downloads from "containers/Downloads";
import { lazy, Suspense, useEffect, useState } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { useAuth } from "@clerk/react";
import axios from "axios";
import {
  // LOGIN,
  CHANGE_PASSWORD,
  DASHBOARD,
  DOWNLOADS,
  LOGIN,
  PREARRIVAL_REGISTRATION,
} from "settings/constants";
import { useIdleLogout } from "hooks/useIdleLogout";
import { getDashboardConfigBatch, getUserHotels } from "./api";
const Login = lazy(() => import("containers/Login/Login"));
const AdminLayout = lazy(() => import("containers/Layout/Layout"));

const NotFound = lazy(() => import("containers/NotFound/NotFound"));
// const GuestSignature = lazy(() => import('containers/GuestSignature'));
const PreArrivalRegistration = lazy(
  () => import("containers/PreArrivalRegistration"),
);

const ChangePassword = lazy(
  () => import("containers/ChangePassword/ChangePassword"),
);

/**
 *
 *  A wrapper for <Route> that redirects to the login
 * screen if you're not yet authenticated.
 *
 */

const Routes = (props: any) => {
  // TOFIX: ADD typings for all variables
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const history = useHistory();
  useIdleLogout(Boolean(isLoaded && isSignedIn));
  const [authenticated, setAuthenticated] = useState(false);
  const [canAccessHotelList, setCanAccessHotelList] = useState([]);
  const [canDownloadHotelList, setCanDownloadHotelList] = useState([]);
  const [hasDownloadRight, setHasDownloadRight] = useState<boolean>(false);
  const [isPreArrival, setIsPreArrival] = useState<boolean>(false);
  const [isFetchingConfig, setIsFetchingConfig] = useState<boolean>(false);

  // Intercept all axios requests to inject the active Clerk token
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(async (config) => {
      if (isSignedIn) {
        try {
          const token = await getToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (e) {
          console.error("Failed to get Clerk token", e);
        }
      }
      return config;
    });
    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, [isSignedIn, getToken]);

  // Sync Clerk's auth state with legacy authenticated state
  useEffect(() => {
    const syncToken = async () => {
      if (isLoaded && isSignedIn && !authenticated) {
        try {
          const token = await getToken();
          if (token) {
            localStorage.ldapToken = token;
          } else {
            localStorage.ldapToken = "clerk-sso-token"; // Temporary fallback
          }
        } catch (e) {
          localStorage.ldapToken = "clerk-sso-token";
        }
        setAuthenticated(true);
      } else if (isLoaded && !isSignedIn && authenticated) {
        // User signed out via Clerk
        localStorage.removeItem("ldapToken");
        setAuthenticated(false);
        setCanAccessHotelList([]);
      }
    };
    syncToken();
  }, [isLoaded, isSignedIn, authenticated, getToken]);

  useEffect(() => {
    if (localStorage.ldapToken && isSignedIn) {
      const getDashboardConfigHandler = async () => {
        // Prevent re-fetching if we already have the data
        if (canAccessHotelList.length > 0) return;

        setIsFetchingConfig(true);
        try {
          const userHotels = await getUserHotels();
          let canAccessList = [];
          let canDownloadList = [];
          let hasPreArrival = false;

          if (!Array.isArray(userHotels) || userHotels.length === 0) {
            console.warn("User has no hotels assigned");
            setCanAccessHotelList([]);
            setCanDownloadHotelList([]);
            setHasDownloadRight(false);
            setIsPreArrival(false);
            setIsFetchingConfig(false);
            return;
          }

          // Fetch all configs in a single batch request
          const hotelIds = userHotels.map(h => h.hotelId);
          const batchConfigs = await getDashboardConfigBatch(hotelIds);

          for (const hotel of userHotels) {
            const dashboardConfig = batchConfigs[hotel.hotelId];
            const defaultPermissions = {
              dashboard: true,
              download: true,
              preArrival: true,
            };

            const config = dashboardConfig || defaultPermissions;

            if (config.dashboard !== false) {
              canAccessList.push({ ...hotel, permission: "dashboard" });
            }
            if (config.download !== false) {
              canDownloadList.push({ ...hotel, permission: "download" });
            }
            if (config.preArrival !== false) {
              hasPreArrival = true;
            }
          }

          setCanAccessHotelList(canAccessList);
          setCanDownloadHotelList(canDownloadList);
          setHasDownloadRight(canDownloadList.length > 0);
          setIsPreArrival(hasPreArrival);
        } catch (err: any) {
          console.error(err);
          // Fallback on API failure - clear access to be safe since we don't know the user's hotels
          setCanAccessHotelList([]);
          setCanDownloadHotelList([]);
          setHasDownloadRight(false);
          setIsPreArrival(false);
        }
        setIsFetchingConfig(false);
      };

      getDashboardConfigHandler();
    }
  }, [authenticated, isSignedIn]);

  useEffect(() => {
    if (isLoaded) {
      if (!isSignedIn && !authenticated) {
        history.push("/login");
      } else if (isSignedIn && history.location.pathname === "/login") {
        history.push(DASHBOARD);
      } else if (isSignedIn && history.location.pathname === "/") {
        history.push(DASHBOARD);
      }
    }
  }, [isLoaded, isSignedIn, authenticated, history]);

  if (isFetchingConfig || !isLoaded || (isSignedIn && !authenticated)) {
    return <InLineLoader />;
  }

  return (
    <Suspense fallback={<InLineLoader />}>
      <Switch>
        <Route exact={true} path={LOGIN}>
          <Suspense fallback={<InLineLoader />}>
            <Login
              {...{ authenticated, setAuthenticated }}
              isLoaded={props.isLoaded}
            />
          </Suspense>
        </Route>

        <Route exact={true} path={DASHBOARD}>
          <AdminLayout
            hasDownloadRight={hasDownloadRight}
            isPreArrival={isPreArrival}
          >
            {canAccessHotelList.length > 0 ? (
              <Suspense fallback={<InLineLoader />}>
                <Dashboard canAccessHotelList={canAccessHotelList} />
              </Suspense>
            ) : (
              <div style={{ padding: "50px", textAlign: "center" }}>
                <h2>No Hotels Assigned</h2>
                <p>
                  You currently do not have access to any hotels. Please contact
                  an administrator.
                </p>
              </div>
            )}
          </AdminLayout>
        </Route>

        <Route exact={true} path={PREARRIVAL_REGISTRATION}>
          <AdminLayout
            hasDownloadRight={hasDownloadRight}
            isPreArrival={isPreArrival}
          >
            <Suspense fallback={<InLineLoader />}>
              <PreArrivalRegistration />
            </Suspense>
          </AdminLayout>
        </Route>

        <Route exact={true} path={DOWNLOADS}>
          <AdminLayout
            hasDownloadRight={hasDownloadRight}
            isPreArrival={isPreArrival}
          >
            <Suspense fallback={<InLineLoader />}>
              <Downloads canDownloadHotelList={canDownloadHotelList} />
            </Suspense>
          </AdminLayout>
        </Route>

        <Route exact={true} path={CHANGE_PASSWORD}>
          <AdminLayout
            hasDownloadRight={hasDownloadRight}
            isPreArrival={isPreArrival}
          >
            <Suspense fallback={<InLineLoader />}>
              <ChangePassword setAuthenticated={setAuthenticated} />
            </Suspense>
          </AdminLayout>
        </Route>

        <Route exact={true} path="/">
          <Redirect to={DASHBOARD} />
        </Route>

        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
};

export default Routes;
